import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { dataLayer } from '../../../data/dataLayer';
import { X, Activity, ArrowUpRight, ArrowDownRight, ArrowRight, Clock, Target, Zap, CheckCircle2, AlertTriangle, UserMinus, Unlink, StickyNote, BarChart3, ClipboardCheck } from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { type ModuleId, MODULES, getModuleIcon } from '../../../content';
import { GroupId } from '../../../config/systemConfig';
import { getRecentSessionsForLearner, getAllModuleTrends, getWeeklyActivityForLearner, getWeeklyStats } from '../../../utils/activityHelpers';
import { getDashboardStats } from '../../../utils/dashboardStats';
import { ActivityTimeline } from '../../../components/shared/ActivityTimeline';
import { WeeklyActivityDots } from '../../../components/shared/WeeklyActivityDots';
import { DjayAvatar } from '../../../mascot';
import { DjayInsightBar } from '../components/DjayInsightBar';
import { AppState } from '../../../app/types';
import { useFocusTrap } from '../../../app/hooks/useFocusTrap';
import { useEscapeKey } from '../../../app/hooks/useEscapeKey';
import { getAssessmentHistory, getReport, type AssessmentHistoryEntry, type AssessmentReport } from '../../../assessment';
import { levelCodeToCitoLevel } from '../../../utils/citoLevelUtils';
import { getStudentInsights } from '../utils/differentiationInsights';
// ============================================================
// Weekly accuracy trend helper (SD-1)
// ============================================================
interface WeekAccuracy {
  label: string;
  accuracy: number;
  questionCount: number;
}
function getWeeklyAccuracyTrend(questionStates: Record<string, {
  history?: Array<{
    timestamp: number;
    isCorrect: boolean;
  }>;
}>, weeks: number = 4): WeekAccuracy[] {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const thisMonday = new Date(now);
  thisMonday.setHours(0, 0, 0, 0);
  thisMonday.setDate(thisMonday.getDate() + mondayOffset);
  const thisMondayTs = thisMonday.getTime();
  const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;
  const weekLabels = ['4w geleden', '3w geleden', '2w geleden', 'Vorige week', 'Deze week'];
  const result: WeekAccuracy[] = [];
  for (let i = weeks; i >= 0; i--) {
    const weekStart = thisMondayTs - i * MS_PER_WEEK;
    const weekEnd = weekStart + MS_PER_WEEK;
    let correct = 0;
    let total = 0;
    for (const state of Object.values(questionStates)) {
      if (!state.history) continue;
      for (const attempt of state.history) {
        if (attempt.timestamp >= weekStart && attempt.timestamp < weekEnd) {
          total++;
          if (attempt.isCorrect) correct++;
        }
      }
    }
    result.push({
      label: weekLabels[weeks - i] || `${i}w`,
      accuracy: total > 0 ? Math.round(correct / total * 100) : 0,
      questionCount: total
    });
  }
  return result;
}
export interface StudentData {
  id: string;
  name: string;
  data: AppState;
}
interface StudentDetailPanelProps {
  student: StudentData;
  selectedGroup: GroupId;
  getModuleMastery: (learnerId: string, moduleId: ModuleId) => number;
  onClose: () => void;
  onViewDashboard?: (learnerId: string) => void;
  isOpen: boolean;
  classroomName?: string;
  onRemoveFromClassroom?: (studentId: string) => void;
  onUnlinkStudent?: (studentId: string) => void;
  onNavigateToAssessments?: (studentName: string) => void;
}
export function StudentDetailPanel({
  student,
  selectedGroup,
  getModuleMastery,
  onClose,
  onViewDashboard,
  isOpen,
  classroomName,
  onRemoveFromClassroom,
  onUnlinkStudent,
  onNavigateToAssessments
}: StudentDetailPanelProps) {
  const [activeTab, setActiveTab] = useState<'overzicht' | 'vakken' | 'activiteit' | 'toets' | 'beheer'>('overzicht');
  const [confirmAction, setConfirmAction] = useState<'remove' | 'unlink' | null>(null);
  const [note, setNote] = useState('');
  const [saveStatus, setSaveStatus] = useState<'saving' | 'saved' | null>(null);
  const [assessmentHistory, setAssessmentHistory] = useState<AssessmentHistoryEntry[]>([]);
  const [assessmentReports, setAssessmentReports] = useState<Record<string, AssessmentReport>>({});
  const [isLoadingAssessments, setIsLoadingAssessments] = useState(false);
  const focusTrapRef = useFocusTrap(isOpen);
  useEscapeKey(onClose);
  const stats = useMemo(() => {
    return getDashboardStats(student.data, MODULES, selectedGroup);
  }, [student.data, selectedGroup]);
  const weeklyActivity = useMemo(() => {
    return getWeeklyActivityForLearner(student.data.questionStates || {});
  }, [student.data.questionStates]);
  const weeklyStats = useMemo(() => {
    return getWeeklyStats(student.data.questionStates || {});
  }, [student.data.questionStates]);
  const moduleMastery = useMemo(() => {
    return MODULES.map(module => {
      const mastery = getModuleMastery(student.id, module.id);
      return {
        module,
        mastery
      };
    }).sort((a, b) => b.mastery - a.mastery);
  }, [student.id, getModuleMastery]);
  const recentSessions = useMemo(() => {
    return getRecentSessionsForLearner(student.data.questionStates || {}, 5);
  }, [student.data.questionStates]);
  const trends = useMemo(() => {
    return getAllModuleTrends(student.data.familyMastery || {}, selectedGroup);
  }, [student.data.familyMastery, selectedGroup]);
  const strengths = moduleMastery.filter(m => m.mastery >= 60).slice(0, 3);
  const weaknesses = [...moduleMastery].reverse().filter(m => m.mastery > 0 && m.mastery < 40).slice(0, 3);
  const differentiationInsights = useMemo(() => {
    return getStudentInsights(student.data, selectedGroup, 3);
  }, [student.data, selectedGroup]);
  const practicedModules = moduleMastery.filter(m => m.mastery > 0);
  const overallMastery = practicedModules.length > 0 ? Math.round(practicedModules.reduce((sum, m) => sum + m.mastery, 0) / practicedModules.length) : 0;
  // Determine Djay's message and emotion
  const getDjayInsight = () => {
    if (differentiationInsights.length > 0) {
      const primaryInsight = differentiationInsights[0];
      return {
        emotion: 'encouraging' as const,
        message: `${student.name} loopt vast bij ${primaryInsight.familyName.toLowerCase()}. ${primaryInsight.recommendation}`
      };
    }
    if (weaknesses.length > 0) {
      return {
        emotion: 'encouraging' as const,
        message: `${student.name} heeft wat extra oefening nodig bij ${weaknesses[0].module.title.toLowerCase()}.`
      };
    }
    if (strengths.length > 0 && overallMastery > 70) {
      return {
        emotion: 'proud' as const,
        message: `Wauw, ${student.name} is ontzettend goed bezig! Vooral ${strengths[0].module.title.toLowerCase()} gaat fantastisch.`
      };
    }
    if (weeklyStats.daysPracticed >= 3) {
      return {
        emotion: 'happy' as const,
        message: `${student.name} heeft een goed oefenritme te pakken deze week!`
      };
    }
    return {
      emotion: 'thinking' as const,
      message: `Laten we samen kijken hoe we ${student.name} verder kunnen helpen groeien.`
    };
  };
  const djayInsight = getDjayInsight();
  // Load notes on mount or student change
  useEffect(() => {
    dataLayer.getTeacherNote(student.id).then(setNote).catch(() => setNote(''));
  }, [student.id]);
  const handleNoteBlur = () => {
    setSaveStatus('saving');
    dataLayer.saveTeacherNote(student.id, note).then(() => {
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    }).catch(() => {
      setSaveStatus(null);
    });
  };
  // Reset confirmation state when panel closes or student changes
  useEffect(() => {
    setConfirmAction(null);
    if (!isOpen) {
      setActiveTab('overzicht');
    }
  }, [isOpen, student.id]);
  useEffect(() => {
    if (activeTab === 'toets') {
      setIsLoadingAssessments(true);
      getAssessmentHistory(selectedGroup).then(history => {
        const filteredHistory = history.filter(entry => entry.learnerId === student.id);
        setAssessmentHistory(filteredHistory);
        Promise.all(filteredHistory.map(entry => getReport(entry.attemptId).then(report => ({
          id: entry.attemptId,
          report
        })))).then(results => {
          const reportsMap: Record<string, AssessmentReport> = {};
          results.forEach(({
            id,
            report
          }) => {
            if (report) reportsMap[id] = report;
          });
          setAssessmentReports(reportsMap);
          setIsLoadingAssessments(false);
        }).catch(() => {
          setIsLoadingAssessments(false);
        });
      }).catch(() => {
        setIsLoadingAssessments(false);
      });
    }
  }, [activeTab, selectedGroup, student.id]);
  const TABS: {
    id: 'overzicht' | 'vakken' | 'activiteit' | 'toets' | 'beheer';
    label: string;
  }[] = [{
    id: 'overzicht',
    label: 'Overzicht'
  }, {
    id: 'vakken',
    label: 'Vakken'
  }, {
    id: 'activiteit',
    label: 'Activiteit'
  }, {
    id: 'toets',
    label: 'Toetsen'
  }, {
    id: 'beheer',
    label: 'Beheer'
  }];
  return <AnimatePresence data-id="element-7354">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-40" data-id="element-7355" />

          {/* Slide-over Panel */}
          <motion.div ref={focusTrapRef as React.RefObject<HTMLDivElement>} initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed inset-y-0 right-0 w-full max-w-2xl bg-slate-50 shadow-2xl z-50 flex flex-col border-l border-slate-200 overflow-hidden" data-id="element-7356">
            {/* Header */}
            <div className="flex-shrink-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between" data-id="element-7357">
              <h2 className="text-lg font-bold text-slate-900" data-id="element-7358">
                {student.name}
              </h2>
              <button onClick={onClose} className="p-2 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-7359">
                <X className="w-5 h-5" data-id="element-7360" />
              </button>
            </div>

            {/* Tab Bar (Sticky) */}
            <div className="flex-shrink-0 bg-white border-b border-slate-200 px-6 flex items-center gap-6 sticky top-0 z-10" data-id="element-7361">
              {TABS.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`py-4 text-sm font-bold transition-colors relative focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded-sm ${activeTab === tab.id ? 'text-indigo-600' : 'text-slate-500 hover:text-slate-800'}`} data-id="element-7362">
                  {tab.label}
                  {activeTab === tab.id && <motion.div layoutId="activeTabIndicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600" initial={false} data-id="element-7363" />}
                </button>)}
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6 relative" data-id="element-7364">
              <AnimatePresence mode="wait" data-id="element-7365">
                {activeTab === 'overzicht' && <motion.div key="overzicht" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="space-y-8" data-id="element-7366">
                    {/* Student Profile Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6" data-id="element-7367">
                      <div className="flex items-center gap-5" data-id="element-7368">
                        <div className="relative" data-id="element-7369">
                          <div className="w-20 h-20 rounded-2xl bg-indigo-600 flex items-center justify-center text-white text-3xl font-black shadow-md transform -rotate-3" data-id="element-7370">
                            {student.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="absolute -bottom-2 -right-2 bg-white rounded-full p-1 shadow-sm border border-slate-100" data-id="element-7371">
                            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center border-2 border-white" data-id="element-7372">
                              <span className="text-xs font-black text-emerald-700" data-id="element-7373">
                                {overallMastery}%
                              </span>
                            </div>
                          </div>
                        </div>
                        <div data-id="element-7374">
                          <h1 className="text-3xl font-black text-slate-900 tracking-tight" data-id="element-7375">
                            {student.name}
                          </h1>
                          <div className="flex items-center gap-3 mt-1.5" data-id="element-7376">
                            <span className="bg-slate-100 text-slate-600 px-2.5 py-1 rounded-lg text-sm font-bold border border-slate-200" data-id="element-7377">
                              Groep {selectedGroup}
                            </span>
                            {onViewDashboard && <button onClick={() => onViewDashboard(student.id)} className="text-sm font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1 transition-colors" data-id="element-7378">
                                Bekijk als leerling{' '}
                                <ArrowRight className="w-3.5 h-3.5" data-id="element-7379" />
                              </button>}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Djay Insight */}
                    <div className="flex items-center gap-4 bg-indigo-50/50 p-4 rounded-2xl border border-indigo-100" data-id="element-7380">
                      <DjayAvatar size="sm" emotion={djayInsight.emotion} data-id="element-7381" />
                      <p className="text-sm font-medium text-indigo-900 leading-relaxed" data-id="element-7382">
                        {djayInsight.message}
                      </p>
                    </div>

                    {/* Quick Stats Row */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-7383">
                      <Card className="p-4 border-slate-200 shadow-sm flex flex-col items-center text-center" data-id="element-7384">
                        <div className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center mb-2" data-id="element-7385">
                          <CheckCircle2 className="w-4 h-4" data-id="element-7386" />
                        </div>
                        <div className="text-xl font-black text-slate-900" data-id="element-7387">
                          {stats.totalQuestions}
                        </div>
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-1" data-id="element-7388">
                          Beantwoord
                        </div>
                      </Card>
                      <Card className="p-4 border-slate-200 shadow-sm flex flex-col items-center text-center" data-id="element-7389">
                        <div className="w-8 h-8 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center mb-2" data-id="element-7390">
                          <Activity className="w-4 h-4" data-id="element-7391" />
                        </div>
                        <div className="text-xl font-black text-slate-900" data-id="element-7392">
                          {Math.round(stats.accuracy * 100)}%
                        </div>
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-1" data-id="element-7393">
                          Nauwkeurigheid
                        </div>
                      </Card>
                      <Card className="p-4 border-slate-200 shadow-sm flex flex-col items-center text-center" data-id="element-7394">
                        <div className="w-8 h-8 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center mb-2" data-id="element-7395">
                          <Zap className="w-4 h-4" data-id="element-7396" />
                        </div>
                        <div className="text-xl font-black text-slate-900" data-id="element-7397">
                          {stats.currentStreak}
                        </div>
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-1" data-id="element-7398">
                          Dagen Streak
                        </div>
                      </Card>
                      <Card className="p-4 border-slate-200 shadow-sm flex flex-col items-center text-center" data-id="element-7399">
                        <div className="w-8 h-8 rounded-full bg-violet-50 text-violet-600 flex items-center justify-center mb-2" data-id="element-7400">
                          <Clock className="w-4 h-4" data-id="element-7401" />
                        </div>
                        <div className="text-xl font-black text-slate-900" data-id="element-7402">
                          {stats.learningTime}
                        </div>
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-1" data-id="element-7403">
                          Minuten
                        </div>
                      </Card>
                    </div>

                    {/* SD-1: Weekly Accuracy Trend */}
                    {(() => {
                const weeklyTrend = getWeeklyAccuracyTrend(student.data.questionStates || {});
                const hasData = weeklyTrend.some(w => w.questionCount > 0);
                if (!hasData) return null;
                return <Card className="p-5 border-slate-200 shadow-sm bg-white" data-id="element-7404">
                          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-7405">
                            <BarChart3 className="w-4 h-4 text-indigo-500" data-id="element-7406" />
                            Trend afgelopen weken
                          </h3>
                          <div className="flex items-end gap-2 h-28" data-id="element-7407">
                            {weeklyTrend.map((week, i) => {
                      const barHeight = week.questionCount > 0 ? Math.max(week.accuracy / 100 * 100, 8) : 0;
                      const barColor = week.accuracy >= 70 ? 'bg-emerald-500' : week.accuracy >= 40 ? 'bg-amber-400' : week.questionCount > 0 ? 'bg-rose-400' : 'bg-slate-200';
                      return <div key={i} className="flex-1 flex flex-col items-center gap-1" data-id="element-7408">
                                  <span className="text-[10px] font-bold text-slate-600 tabular-nums" data-id="element-7409">
                                    {week.questionCount > 0 ? `${week.accuracy}%` : '—'}
                                  </span>
                                  <div className="w-full flex items-end justify-center h-20" data-id="element-7410">
                                    <motion.div initial={{
                            height: 0
                          }} animate={{
                            height: `${barHeight}%`
                          }} transition={{
                            duration: 0.6,
                            delay: i * 0.08,
                            ease: 'easeOut'
                          }} className={`w-full max-w-[40px] rounded-t-md ${barColor}`} data-id="element-7411" />
                                  </div>
                                  <span className="text-[9px] font-bold text-slate-400 text-center leading-tight" data-id="element-7412">
                                    {week.label}
                                  </span>
                                </div>;
                    })}
                          </div>
                        </Card>;
              })()}

                    {/* Strengths & Weaknesses */}
                    {(strengths.length > 0 || differentiationInsights.length > 0) && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-7413">
                        {strengths.length > 0 && <Card className="p-5 border-emerald-100 bg-emerald-50/30 shadow-sm" data-id="element-7414">
                            <h3 className="text-xs font-bold text-emerald-800 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-7415">
                              <ArrowUpRight className="w-4 h-4" data-id="element-7416" /> Sterk in
                            </h3>
                            <div className="space-y-2.5" data-id="element-7417">
                              {strengths.map(s => {
                      const IconComp = getModuleIcon(s.module.icon);
                      return <div key={s.module.id} className="flex items-center justify-between bg-white px-3 py-2.5 rounded-xl border border-emerald-100 shadow-sm text-sm" data-id="element-7418">
                                    <div className="flex items-center gap-2.5 text-slate-700 font-medium" data-id="element-7419">
                                      <IconComp className="w-4 h-4 text-emerald-500" data-id="element-7420" />
                                      <span className="truncate" data-id="element-7421">
                                        {s.module.title}
                                      </span>
                                    </div>
                                    <span className="font-black text-emerald-700 ml-2" data-id="element-7422">
                                      {s.mastery}%
                                    </span>
                                  </div>;
                    })}
                            </div>
                          </Card>}
                        {differentiationInsights.length > 0 && <Card className="p-5 border-rose-100 bg-rose-50/30 shadow-sm" data-id="element-7423">
                            <h3 className="text-xs font-bold text-rose-800 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-7424">
                              <AlertTriangle className="w-4 h-4" data-id="element-7425" />{' '}
                              Differentiatie Inzicht
                            </h3>
                            <div className="space-y-3" data-id="element-7426">
                              {differentiationInsights.map(insight => {
                      const moduleDef = MODULES.find(m => m.id === insight.moduleId);
                      const IconComp = getModuleIcon(moduleDef?.icon || 'calculator');
                      return <div key={insight.familyId} className="bg-white p-3 rounded-xl border border-rose-100 shadow-sm text-sm flex flex-col gap-2" data-id="element-7427">
                                    <div className="flex items-center justify-between" data-id="element-7428">
                                      <div className="flex items-center gap-2.5 text-slate-700 font-medium" data-id="element-7429">
                                        <IconComp className="w-4 h-4 text-rose-500" data-id="element-7430" />
                                        <span className="truncate font-bold" data-id="element-7431">
                                          {insight.familyName}
                                        </span>
                                      </div>
                                      <span className="font-black text-rose-600 ml-2" data-id="element-7432">
                                        {insight.mastery}%
                                      </span>
                                    </div>
                                    <p className="text-xs text-slate-600 leading-relaxed" data-id="element-7433">
                                      {insight.recommendation}
                                    </p>
                                  </div>;
                    })}
                            </div>
                          </Card>}
                      </div>}
                  </motion.div>}

                {activeTab === 'vakken' && <motion.div key="vakken" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} data-id="element-7434">
                    {/* Module Mastery List */}
                    <Card className="border-slate-200 shadow-sm overflow-hidden" data-id="element-7435">
                      <div className="p-5 border-b border-slate-100 bg-white" data-id="element-7436">
                        <h3 className="text-base font-bold text-slate-900 flex items-center gap-2" data-id="element-7437">
                          <Target className="w-5 h-5 text-indigo-500" data-id="element-7438" />
                          Vakbeheersing
                        </h3>
                      </div>

                      <div className="divide-y divide-slate-100 bg-white" data-id="element-7439">
                        {practicedModules.length > 0 ? practicedModules.map(({
                    module,
                    mastery
                  }) => {
                    const trend = trends[module.id];
                    const IconComp = getModuleIcon(module.icon);
                    const barColor = mastery >= 60 ? 'bg-emerald-500' : mastery >= 40 ? 'bg-amber-400' : 'bg-rose-500';
                    const textColor = mastery >= 60 ? 'text-emerald-700' : mastery >= 40 ? 'text-amber-600' : 'text-rose-600';
                    return <div key={module.id} className="p-4 hover:bg-slate-50 transition-colors flex flex-col sm:flex-row sm:items-center gap-4" data-id="element-7440">
                                <div className="flex items-center gap-3 sm:w-1/3 flex-shrink-0" data-id="element-7441">
                                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-7442">
                                    <IconComp className="w-4 h-4 text-slate-500" data-id="element-7443" />
                                  </div>
                                  <div className="font-bold text-slate-800 text-sm truncate" data-id="element-7444">
                                    {module.title}
                                  </div>
                                </div>

                                <div className="flex-1 flex items-center gap-4" data-id="element-7445">
                                  <div className="flex-1 bg-slate-100 rounded-full h-2.5 overflow-hidden" data-id="element-7446">
                                    <motion.div initial={{
                            width: 0
                          }} animate={{
                            width: `${mastery}%`
                          }} transition={{
                            duration: 0.8,
                            ease: 'easeOut'
                          }} className={`h-full rounded-full ${barColor}`} data-id="element-7447" />
                                  </div>
                                  <div className={`w-10 text-right font-black text-sm ${textColor}`} data-id="element-7448">
                                    {mastery}%
                                  </div>
                                  <div className="w-20 flex justify-end" data-id="element-7449">
                                    {trend === 'up' && <span className="text-[10px] font-bold text-emerald-700 flex items-center bg-emerald-50 px-2 py-1 rounded-md uppercase tracking-wider" data-id="element-7450">
                                        <ArrowUpRight className="w-3 h-3 mr-1" data-id="element-7451" />{' '}
                                        Stijgt
                                      </span>}
                                    {trend === 'down' && <span className="text-[10px] font-bold text-rose-600 flex items-center bg-rose-50 px-2 py-1 rounded-md uppercase tracking-wider" data-id="element-7452">
                                        <ArrowDownRight className="w-3 h-3 mr-1" data-id="element-7453" />{' '}
                                        Daalt
                                      </span>}
                                    {trend === 'stable' && <span className="text-[10px] font-bold text-slate-400 flex items-center bg-slate-100 px-2 py-1 rounded-md uppercase tracking-wider" data-id="element-7454">
                                        <ArrowRight className="w-3 h-3 mr-1" data-id="element-7455" />{' '}
                                        Stabiel
                                      </span>}
                                  </div>
                                </div>
                              </div>;
                  }) : <div className="p-8 text-center text-slate-500" data-id="element-7456">
                            Nog geen vakken geoefend.
                          </div>}
                      </div>
                    </Card>
                  </motion.div>}

                {activeTab === 'activiteit' && <motion.div key="activiteit" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-7457">
                    {/* Recent Activity */}
                    <Card className="p-6 border-slate-200 shadow-sm" data-id="element-7458">
                      <h3 className="text-base font-bold text-slate-900 mb-5 flex items-center gap-2" data-id="element-7459">
                        <Activity className="w-5 h-5 text-indigo-500" data-id="element-7460" />
                        Activiteit deze week
                      </h3>
                      <div className="flex justify-center" data-id="element-7461">
                        <WeeklyActivityDots weeklyActivity={weeklyActivity} color="indigo" data-id="element-7462" />
                      </div>
                    </Card>

                    <Card className="p-6 border-slate-200 shadow-sm" data-id="element-7463">
                      <h3 className="text-base font-bold text-slate-900 mb-5 flex items-center gap-2" data-id="element-7464">
                        <Clock className="w-5 h-5 text-indigo-500" data-id="element-7465" />
                        Laatste sessies
                      </h3>
                      <ActivityTimeline sessions={recentSessions} data-id="element-7466" />
                    </Card>
                  </motion.div>}

                {activeTab === 'toets' && <motion.div key="toets" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="space-y-6" data-id="element-7467">
                    {/* Djay Insight Bar */}
                    <DjayInsightBar emotion={assessmentHistory.length === 0 ? 'thinking' : 'happy'} message={assessmentHistory.length === 0 ? `${student.name} heeft nog geen toetsen gemaakt.` : `${student.name} heeft ${assessmentHistory.length} toets(en) gemaakt. Resultaten geven een indicatie van het niveau op basis van de Meester djay taxonomie.`} data-id="element-7468" />

                    <Card className="border-slate-200 shadow-sm overflow-hidden" data-id="element-7469">
                      <div className="p-5 border-b border-slate-100 bg-white flex items-center justify-between" data-id="element-7470">
                        <h3 className="text-base font-bold text-slate-900 flex items-center gap-2" data-id="element-7471">
                          <ClipboardCheck className="w-5 h-5 text-indigo-500" data-id="element-7472" />
                          Toetsresultaten
                        </h3>
                        {onNavigateToAssessments && assessmentHistory.length > 0 && <Button variant="outline" size="sm" onClick={() => onNavigateToAssessments(student.name)} className="text-indigo-600 border-indigo-200 hover:bg-indigo-50" data-id="element-7473">
                              <ArrowRight className="w-4 h-4 mr-2" data-id="element-7474" />
                              Bekijk alle toetsen
                            </Button>}
                      </div>

                      <div className="divide-y divide-slate-100 bg-white" data-id="element-7475">
                        {isLoadingAssessments ? <div className="p-8 text-center text-slate-500" data-id="element-7476">
                            Laden...
                          </div> : assessmentHistory.length > 0 ? assessmentHistory.map(entry => {
                    const report = assessmentReports[entry.attemptId];
                    return <div key={entry.attemptId} className="p-5 hover:bg-slate-50 transition-colors" data-id="element-7477">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4" data-id="element-7478">
                                  <div data-id="element-7479">
                                    <div className="font-bold text-slate-900" data-id="element-7480">
                                      {new Date(entry.completedAt).toLocaleDateString('nl-NL', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                                    </div>
                                    <div className="text-sm text-slate-500 mt-1" data-id="element-7481">
                                      {entry.totalQuestions} vragen •{' '}
                                      {Math.round(entry.totalTimeSeconds / 60)}{' '}
                                      minuten
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-4" data-id="element-7482">
                                    <div className="text-right" data-id="element-7483">
                                      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-7484">
                                        Score
                                      </div>
                                      <div className={`text-xl font-black ${entry.score >= 70 ? 'text-emerald-600' : entry.score >= 50 ? 'text-amber-600' : 'text-rose-600'}`} data-id="element-7485">
                                        {entry.score}%
                                      </div>
                                    </div>
                                    {report && <div className="text-right" data-id="element-7486">
                                        <div className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-7487">
                                          Niveau
                                        </div>
                                        <div className="flex items-center justify-end gap-2" data-id="element-7488">
                                          <div className="text-xl font-black text-indigo-600" data-id="element-7489">
                                            {report.overallEstimatedLevelCode}
                                          </div>
                                          {(() => {
                                const cito = levelCodeToCitoLevel(report.overallEstimatedLevelCode, report.selectedGroup);
                                if (!cito) return null;
                                return <div className={`text-xs font-bold px-2 py-0.5 rounded border ${cito.color} ${cito.bgColor} ${cito.borderColor}`} data-id="element-7490">
                                                {cito.label}
                                              </div>;
                              })()}
                                        </div>
                                      </div>}
                                  </div>
                                </div>

                                {report && report.byModule && report.byModule.length > 0 && <div className="mt-4 pt-4 border-t border-slate-100" data-id="element-7491">
                                      <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-7492">
                                        Per Vak
                                      </h4>
                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-7493">
                                        {report.byModule.map(m => {
                            const moduleInfo = MODULES.find(mod => mod.id === m.moduleId);
                            if (!moduleInfo) return null;
                            const IconComp = getModuleIcon(moduleInfo.icon);
                            const cito = levelCodeToCitoLevel(m.estimatedLevelCode ?? '', report.selectedGroup);
                            return <div key={m.moduleId} className="flex items-center justify-between bg-white border border-slate-200 rounded-lg p-3" data-id="element-7494">
                                              <div className="flex items-center gap-2" data-id="element-7495">
                                                <IconComp className="w-4 h-4 text-slate-400" data-id="element-7496" />
                                                <span className="text-sm font-bold text-slate-700" data-id="element-7497">
                                                  {moduleInfo.title}
                                                </span>
                                              </div>
                                              <div className="flex items-center gap-3" data-id="element-7498">
                                                <span className="text-sm font-black text-slate-900" data-id="element-7499">
                                                  {Math.round(m.scorePercentage ?? m.accuracy)}
                                                  %
                                                </span>
                                                <div className="flex items-center gap-1.5" data-id="element-7500">
                                                  <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100" data-id="element-7501">
                                                    {m.estimatedLevelCode}
                                                  </span>
                                                  {cito && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${cito.color} ${cito.bgColor} ${cito.borderColor}`} data-id="element-7502">
                                                      {cito.label}
                                                    </span>}
                                                </div>
                                              </div>
                                            </div>;
                          })}
                                      </div>
                                    </div>}
                              </div>;
                  }) : <div className="p-8 flex flex-col items-center justify-center text-center" data-id="element-7503">
                            <div className="mb-3" data-id="element-7504">
                              <DjayAvatar size="md" emotion="encouraging" data-id="element-7505" />
                            </div>
                            <h4 className="text-sm font-bold text-slate-900 mb-1" data-id="element-7506">
                              Nog geen toetsen
                            </h4>
                            <p className="text-xs text-slate-500 max-w-[220px]" data-id="element-7507">
                              {student.name} heeft nog geen toetsen gemaakt. Zet
                              een toets klaar of laat een niveaubepaling doen!
                            </p>
                          </div>}
                      </div>
                    </Card>
                  </motion.div>}

                {activeTab === 'beheer' && <motion.div key="beheer" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} data-id="element-7508">
                    <div className="mb-8" data-id="element-7509">
                      <h3 className="text-base font-bold text-slate-900 mb-2 flex items-center gap-2" data-id="element-7510">
                        <StickyNote className="w-5 h-5 text-indigo-500" data-id="element-7511" />
                        Persoonlijke Notities
                      </h3>
                      <p className="text-sm text-slate-500 leading-relaxed mb-3" data-id="element-7512">
                        Deze notities zijn alleen zichtbaar voor jou. Gebruik
                        dit voor observaties of aandachtspunten.
                      </p>
                      <div className="relative" data-id="element-7513">
                        <textarea value={note} onChange={e => setNote(e.target.value)} onBlur={handleNoteBlur} placeholder="Typ hier je notities over deze leerling..." className="w-full h-32 p-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none" data-id="element-7514" />
                        <AnimatePresence data-id="element-7515">
                          {saveStatus === 'saved' && <motion.div initial={{
                      opacity: 0,
                      y: 5
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0
                    }} className="absolute bottom-3 right-3 flex items-center gap-1 text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md" data-id="element-7516">
                              <CheckCircle2 className="w-3 h-3" data-id="element-7517" />
                              Opgeslagen
                            </motion.div>}
                        </AnimatePresence>
                      </div>
                    </div>

                    <div className="mb-6" data-id="element-7518">
                      <h3 className="text-base font-bold text-slate-900 mb-2" data-id="element-7519">
                        Leerlingbeheer
                      </h3>
                      <p className="text-sm text-slate-500 leading-relaxed" data-id="element-7520">
                        Beheer de status van deze leerling. Voor het verwijderen
                        of ontkoppelen van leerlingen, ga naar de Klas Beheer
                        pagina.
                      </p>
                    </div>

                    {/* Danger Zone / Beheer */}
                    <div className="space-y-3" data-id="element-7521">
                      {classroomName && onRemoveFromClassroom && <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-7522">
                          <div data-id="element-7523">
                            <div className="font-bold text-slate-900 text-sm" data-id="element-7524">
                              Verwijder uit klas
                            </div>
                            <div className="text-xs text-slate-500 mt-1" data-id="element-7525">
                              Haal {student.name} uit de klas "{classroomName}".
                              De leerling blijft wel gekoppeld aan jouw account.
                            </div>
                          </div>

                          {confirmAction === 'remove' ? <div className="flex items-center gap-2 shrink-0" data-id="element-7526">
                              <Button variant="secondary" size="sm" onClick={() => setConfirmAction(null)} data-id="element-7527">
                                Annuleer
                              </Button>
                              <Button className="bg-rose-600 hover:bg-rose-700 text-white" size="sm" onClick={() => {
                      onRemoveFromClassroom(student.id);
                      setConfirmAction(null);
                    }} data-id="element-7528">
                                Bevestig
                              </Button>
                            </div> : <Button variant="secondary" size="sm" className="shrink-0 text-slate-600 hover:text-rose-600 hover:bg-rose-50 hover:border-rose-200" onClick={() => setConfirmAction('remove')} data-id="element-7529">
                              <UserMinus className="w-4 h-4 mr-2" data-id="element-7530" />
                              Verwijder uit klas
                            </Button>}
                        </div>}

                      {onUnlinkStudent && <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-7531">
                          <div data-id="element-7532">
                            <div className="font-bold text-rose-700 text-sm" data-id="element-7533">
                              Ontkoppel leerling
                            </div>
                            <div className="text-xs text-slate-500 mt-1" data-id="element-7534">
                              Verbreek de koppeling met {student.name} volledig.
                              Je hebt hierna geen toegang meer tot de voortgang.
                            </div>
                          </div>

                          {confirmAction === 'unlink' ? <div className="flex items-center gap-2 shrink-0" data-id="element-7535">
                              <Button variant="secondary" size="sm" onClick={() => setConfirmAction(null)} data-id="element-7536">
                                Annuleer
                              </Button>
                              <Button className="bg-rose-600 hover:bg-rose-700 text-white" size="sm" onClick={() => {
                      onUnlinkStudent(student.id);
                      setConfirmAction(null);
                    }} data-id="element-7537">
                                Bevestig ontkoppeling
                              </Button>
                            </div> : <Button variant="secondary" size="sm" className="shrink-0 text-rose-600 hover:bg-rose-50 border-rose-200" onClick={() => setConfirmAction('unlink')} data-id="element-7538">
                              <Unlink className="w-4 h-4 mr-2" data-id="element-7539" />
                              Ontkoppel
                            </Button>}
                        </div>}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}